/**
 * ALIGN ON OPENINGS (2026-09-30, Matt: "use align on references in doors … the door's
 * frames as reference points for alignments and also the door's midpoint as a
 * reference plane, too. This would allow me to quickly align doors across hallways
 * from each other. Same with windows.")
 *
 * A door/window offers FIVE reference lines to the Align tool, each running ACROSS its
 * host wall (perpendicular to the wall, spanning the wall's thickness): both frame
 * outside edges (the opening's full width — `widthFt` is measured outside the frames,
 * the hostedFootprintCorners convention), both jamb faces (the frame width in from
 * each edge, `resolveOpeningFrame`), and the centerline (hostOffsetFt itself — the
 * station IS the opening's center). As the reference, any of them is the line other
 * elements move onto; as the target, the opening slides ALONG ITS OWN WALL until its
 * picked line lands on the reference — the only way a hosted element can move.
 */
import type { Path2D, Pt2 } from './types'
import type { ResolvedElement } from './params'
import { flattenPathPts } from './arc'
import { polylinePointAt } from './pathOps'
import { centerlineOffsetFt } from './locationLine'
import { resolveOpeningFrame } from './openingFrame'

export interface OpeningAlignLine {
  id: string
  label: string
  a: Pt2
  b: Pt2
  /** the line's station relative to the opening's own center (hostOffsetFt), feet */
  rel: number
}

function hostFrame(host: ResolvedElement | null | undefined) {
  const hp = host?.params.path as Path2D | null | undefined
  if (!host || !hp?.nodes || hp.nodes.length < 2) return null
  const pts = flattenPathPts(hp)
  let len = 0
  for (let i = 0; i < pts.length - 1; i++) len += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
  return {
    pts, len,
    t: host.assemblyThicknessFt || 0.5,
    delta: centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly),
  }
}

/** every Align line a door/window offers, or [] when it has no usable host */
export function openingAlignLines(
  host: ResolvedElement | null | undefined, el: ResolvedElement,
): OpeningAlignLine[] {
  if (el.category !== 'door' && el.category !== 'window') return []
  const hf = hostFrame(host)
  if (!hf) return []
  const s = (el.params.hostOffsetFt as number) || 0
  const w = (el.params.widthFt as number) || 3
  const fw = Math.min(resolveOpeningFrame(el.category, { ...el.typeParams, ...el.params }).widthFt, w / 3)
  const noun = el.category
  const at = (st: number, off: number): Pt2 => {
    const { pt, dir } = polylinePointAt(hf.pts, st)
    return [pt[0] - dir[1] * off, pt[1] + dir[0] * off]
  }
  const line = (rel: number, label: string): OpeningAlignLine => ({
    id: el.id, label, rel,
    a: at(s + rel, hf.delta - hf.t / 2),
    b: at(s + rel, hf.delta + hf.t / 2),
  })
  return [
    line(-w / 2, `${noun} frame edge`),
    line(-w / 2 + fw, `${noun} jamb face`),
    line(0, `${noun} centerline`),
    line(w / 2 - fw, `${noun} jamb face`),
    line(w / 2, `${noun} frame edge`),
  ]
}

/** the line nearest a plan point (measured along the wall — the lines are parallel) */
export function nearestOpeningAlignLine(lines: OpeningAlignLine[], raw: Pt2): OpeningAlignLine | null {
  let best: OpeningAlignLine | null = null
  let bestD = Infinity
  for (const ln of lines) {
    const dx = ln.b[0] - ln.a[0], dy = ln.b[1] - ln.a[1]
    const l = Math.hypot(dx, dy) || 1
    const d = Math.abs((raw[0] - ln.a[0]) * dy - (raw[1] - ln.a[1]) * dx) / l
    if (d < bestD) { bestD = d; best = ln }
  }
  return best
}

/**
 * The new hostOffsetFt that puts the opening's line at `rel` ON the reference line —
 * `{ hostOffsetFt }`, or `{ error }` when the reference doesn't cross the wall
 * (within ~10° of the opening's own lines) or the result would push the opening off
 * the end of its wall.
 */
export function openingOffsetToAlign(
  host: ResolvedElement | null | undefined, el: ResolvedElement, rel: number, ref: { a: Pt2; b: Pt2 },
): { hostOffsetFt: number } | { error: string } {
  const hf = hostFrame(host)
  if (!hf) return { error: 'That opening has no wall to slide along' }
  const s = (el.params.hostOffsetFt as number) || 0
  const w = (el.params.widthFt as number) || 3
  const rd = [ref.b[0] - ref.a[0], ref.b[1] - ref.a[1]]
  const rl = Math.hypot(rd[0], rd[1]) || 1
  const n: Pt2 = [-rd[1] / rl, rd[0] / rl]
  // walk the wall's own centerline station (straight segments slide exactly; a curved
  // wall converges in a few Newton steps along its flattened facets)
  let st = s + rel
  for (let it = 0; it < 8; it++) {
    const { pt, dir } = polylinePointAt(hf.pts, st)
    const along = dir[0] * n[0] + dir[1] * n[1]
    // the opening's lines run ACROSS the wall; the reference must too (~10° gate —
    // the same tolerance the wall Align uses)
    if (Math.abs(along) < 0.985) {
      return { error: `That reference doesn't cross the ${el.category}'s wall — pick one running the same way as its frame lines` }
    }
    const gap = (ref.a[0] - pt[0]) * n[0] + (ref.a[1] - pt[1]) * n[1]
    if (Math.abs(gap) < 1e-9) break
    st += gap / along
  }
  const next = st - rel
  if (next - w / 2 < -1e-6 || next + w / 2 > hf.len + 1e-6) {
    return { error: `That would push the ${el.category} past the end of its wall` }
  }
  return { hostOffsetFt: Math.max(0, next) }
}
